import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractorApi } from '../../lib/contractorApi';
import { useContractorAuthStore } from '../../lib/contractorAuth';

interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'manager' | 'member';
  permissions: string;
  invite_accepted: number;
  created_at: string;
}

const ROLE_COLORS: Record<string, { bg: string; text: string }> = {
  owner:   { bg: '#fef9c3', text: '#854d0e' },
  manager: { bg: '#eff6ff', text: '#1d4ed8' },
  member:  { bg: '#f9fafb', text: '#374151' },
};

function RoleBadge({ role }: { role: string }) {
  const cfg = ROLE_COLORS[role] ?? ROLE_COLORS['member']!;
  return (
    <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: cfg.bg, color: cfg.text, textTransform: 'capitalize' }}>
      {role}
    </span>
  );
}

export function TeamManagement() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const contractor = useContractorAuthStore(s => s.contractor);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<'manager' | 'member'>('member');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['team'],
    queryFn: () => contractorApi.get<{ data: TeamMember[] }>('/contractor/team'),
  });

  const inviteMutation = useMutation({
    mutationFn: (body: { email: string; name: string; role: 'manager' | 'member' }) =>
      contractorApi.post('/contractor/team', body),
    onSuccess: () => {
      setInviteSuccess(`Invite sent to ${inviteEmail}.`);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('member');
      setShowInvite(false);
      void queryClient.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (err) => setInviteError(err instanceof Error ? err.message : 'Failed to send invite.'),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => contractorApi.delete(`/contractor/team/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['team'] }),
    onError: (err) => setInviteError(err instanceof Error ? err.message : 'Failed to remove member.'),
  });

  if (contractor?.tier !== 'enterprise' && contractor?.tier !== 'verified_partner') {
    return (
      <>
        <style>{`@media (min-width: 768px) { .tm-layout { padding-left: 256px !important; } }`}</style>
        <div className="tm-layout" style={{ minHeight: '100dvh', background: '#f9fafb' }}>
          <div style={{ background: '#1D4ED8', padding: '48px 20px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" onClick={() => navigate(-1)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 36, height: 36, borderRadius: 10, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&#8592;</button>
            <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 }}>Team Management</h1>
          </div>
          <div style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 400, margin: '0 auto' }}>
              <p style={{ fontSize: 32, margin: '0 0 12px' }}>🏢</p>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>Enterprise feature</h2>
              <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 20px' }}>Team management is available on the Enterprise plan. Add multiple team members with different permission levels.</p>
              <a href="/contractor/signup?plan=enterprise" style={{ display: 'inline-block', background: '#1D4ED8', color: '#fff', padding: '12px 24px', borderRadius: 10, fontWeight: 700, textDecoration: 'none' }}>Upgrade to Enterprise</a>
            </div>
          </div>
        </div>
      </>
    );
  }

  const members = data?.data ?? [];

  return (
    <>
      <style>{`@media (min-width: 768px) { .tm-layout { padding-left: 256px !important; } }`}</style>
      <div className="tm-layout" style={{ minHeight: '100dvh', background: '#f9fafb' }}>
        {/* Header */}
        <div style={{ background: '#1D4ED8', padding: '48px 20px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" onClick={() => navigate(-1)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 36, height: 36, borderRadius: 10, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&#8592;</button>
            <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 }}>Team Management</h1>
          </div>
          <button
            type="button"
            onClick={() => { setShowInvite(true); setInviteError(null); }}
            style={{ background: '#fff', color: '#1D4ED8', border: 'none', padding: '8px 16px', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
          >
            + Invite
          </button>
        </div>

        <div style={{ padding: '20px 16px 100px', maxWidth: 700, margin: '0 auto' }}>
          {inviteSuccess && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
              <p style={{ margin: 0, color: '#16a34a', fontSize: 14 }}>{inviteSuccess}</p>
            </div>
          )}
          {inviteError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
              <p style={{ margin: 0, color: '#dc2626', fontSize: 14 }}>{inviteError}</p>
            </div>
          )}

          {/* Member list */}
          <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
            {isLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
            ) : members.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <p style={{ color: '#9ca3af', fontSize: 14 }}>No team members yet. Invite your first team member.</p>
              </div>
            ) : (
              members.map((m, i) => (
                <div key={m.id} style={{ padding: '14px 16px', borderBottom: i < members.length - 1 ? '1px solid #f3f4f6' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#111827' }}>{m.name}</p>
                      <RoleBadge role={m.role} />
                      {!m.invite_accepted && (
                        <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>Pending</span>
                      )}
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>{m.email}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9ca3af' }}>{m.permissions.split(',').join(' · ')}</p>
                  </div>
                  {m.role !== 'owner' && (
                    <button
                      type="button"
                      onClick={() => { if (confirm(`Remove ${m.name} from your team?`)) removeMutation.mutate(m.id); }}
                      style={{ background: 'none', border: '1px solid #fecaca', color: '#dc2626', padding: '6px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Invite modal */}
        {showInvite && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'flex-end' }}
            onClick={() => setShowInvite(false)}>
            <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px 40px', width: '100%', maxWidth: 560, margin: '0 auto' }}
              onClick={e => e.stopPropagation()}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 20px' }}>Invite team member</h2>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Name</label>
                <input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Jane Smith" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 15, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Email</label>
                <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="jane@example.com" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 15, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Role</label>
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value as 'manager' | 'member')} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 15, boxSizing: 'border-box' }}>
                  <option value="member">Member — view leads, manage jobs</option>
                  <option value="manager">Manager — full access except billing</option>
                </select>
              </div>
              {inviteError && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{inviteError}</p>}
              <button
                type="button"
                disabled={inviteMutation.isPending || !inviteEmail || !inviteName}
                onClick={() => inviteMutation.mutate({ email: inviteEmail, name: inviteName, role: inviteRole })}
                style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: inviteMutation.isPending ? '#93c5fd' : '#1D4ED8', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
              >
                {inviteMutation.isPending ? 'Sending invite…' : 'Send invite'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
